import { toaster } from "@/components/ui/toaster";
import { AppRoutes } from "@/constants/routes";
import {
  ServiceDetail,
  ServiceDosisUnity,
  UpdateServiceBody,
  deleteService,
  getServiceDetail,
  updateService,
} from "@/services/service-master.service";
import { getAxiosErrorMessage } from "@/utils/axios-error-message";
import { formatCurrencyInputToNumber } from "@/utils/query-string";
import { handlePermissionError } from "@/utils/permission-check";
import { getBooleanValue } from "@/utils/request-validations";
import { Dispatch, SetStateAction } from "react";
import { NavigateFunction } from "react-router-dom";
import { ServiceMasterCreationForm } from "../../create/schema/service-master-creation.schema";
import {
  getSelectedValue,
  getServiceMasterErrorMessage,
  isDosisUnidad,
  parseDecimalInput,
} from "../../utils/service-master-form";

interface FetchServiceHandlerProps {
  id: number;
  setLoadingService: Dispatch<SetStateAction<boolean>>;
  setService: Dispatch<SetStateAction<ServiceDetail | null>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
}

export const fetchServiceHandler = async ({
  id,
  setLoadingService,
  setService,
  navigate,
  fetchPermissions,
}: FetchServiceHandlerProps) => {
  try {
    setLoadingService(true);

    const response = await getServiceDetail(id);

    setService(response ?? null);
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.SERVICES,
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getAxiosErrorMessage(error, "No se pudo obtener el servicio."),
      });
    }

    throw error;
  } finally {
    setLoadingService(false);
  }
};

interface UpdateServiceHandlerProps {
  id: number;
  values: ServiceMasterCreationForm;
  setLoadingUpdate: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  returnPage?: number;
}

export const updateServiceHandler = async ({
  id,
  values,
  setLoadingUpdate,
  navigate,
  fetchPermissions,
  returnPage,
}: UpdateServiceHandlerProps) => {
  const isDosis = isDosisUnidad(values?.unidadCotizacion);

  const cotizacionesDosis: ServiceDosisUnity[] = isDosis
    ? (values?.cotizacionesDosis ?? []).map((row) => ({
        ...(row?.id !== undefined ? { id: row.id } : {}),
        modalidad: Number(getSelectedValue(row?.modalidad)),
        dosisDesde: parseDecimalInput(row?.dosisDesde) ?? 0,
        dosisHasta: parseDecimalInput(row?.dosisHasta) ?? 0,
        valorUnitario: formatCurrencyInputToNumber(row?.valorUnitario ?? ""),
      }))
    : [];

  const body: UpdateServiceBody = {
    id,
    titulo: String(values?.titulo ?? "").trim(),
    descripcion: String(values?.descripcion ?? "").trim(),
    valorUnitario: isDosis
      ? 0
      : formatCurrencyInputToNumber(values?.valorUnitario ?? ""),
    categoria: Number(getSelectedValue(values?.categoria)),
    unidadCotizacion: Number(getSelectedValue(values?.unidadCotizacion)),
    activo: getBooleanValue(values?.activo),
    cotizacionesDosis,
  };

  try {
    setLoadingUpdate(true);

    await updateService(body);

    toaster.create({
      type: "success",
      title: "Servicio editado exitosamente.",
    });

    navigate(AppRoutes.SERVICES, { state: { page: returnPage } });
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.SERVICES,
      { page: returnPage },
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getServiceMasterErrorMessage(
          error,
          "No se ha podido editar el servicio.",
        ),
      });
    }

    throw error;
  } finally {
    setLoadingUpdate(false);
  }
};

interface DeleteServiceHandlerProps {
  id: number;
  setLoadingDelete: Dispatch<SetStateAction<boolean>>;
  navigate: NavigateFunction;
  fetchPermissions: () => Promise<void>;
  returnPage?: number;
}

export const deleteServiceHandler = async ({
  id,
  setLoadingDelete,
  navigate,
  fetchPermissions,
  returnPage,
}: DeleteServiceHandlerProps) => {
  try {
    setLoadingDelete(true);

    await deleteService(id);

    toaster.create({
      type: "success",
      title: "Servicio eliminado exitosamente.",
    });

    navigate(AppRoutes.SERVICES, { state: { page: returnPage } });
  } catch (error) {
    const handled = await handlePermissionError(
      error,
      navigate,
      fetchPermissions,
      AppRoutes.SERVICES,
      { page: returnPage },
    );

    if (!handled) {
      toaster.create({
        type: "error",
        title: getServiceMasterErrorMessage(
          error,
          "No se ha podido eliminar el servicio.",
        ),
      });
    }

    throw error;
  } finally {
    setLoadingDelete(false);
  }
};
